import { observer } from 'mobx-react-lite'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onOpenGlobalSearch } from './globalSearchBus'
import { openPaletteRow } from './openResult'
import { matchPageRows } from './pageRegistry'
import type { PageRow } from './pageRegistry'
import { PaletteEmptyState } from './PaletteEmptyState'
import { PaletteSkeleton } from './PaletteSkeleton'
import type { PaletteRow } from './paletteRow'
import { paletteRowValue } from './paletteRow'
import {
  ActivityRowContent,
  ContactRowContent,
  CreateContactRowContent,
  PageRowContent,
  SeeAllRowContent,
  ThreadRowContent,
} from './PaletteRows'
import { isSearchable, parsePaletteQuery } from './paletteSearch'
import { useGlobalSearch } from './useGlobalSearch'
import { activitiesExclusionTerm } from 'lib/activitiesExclusion'
import Spinner from 'app/components/Spinner'
import { QuickCreateContactModal } from 'app/components/contacts/quick-create/QuickCreateContactModal'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { showModal } from 'app/lib/modal'
import type { ContactJson, UserJson } from 'app/types/entities'
import { useUser } from 'app/utils'

// Short on purpose: the empty-state panel below already says what can be found.
export const INPUT_PLACEHOLDER = `Search Anything`
export const NO_MATCH_COPY = `No contacts or activities match`
export const UNAVAILABLE_COPY = `Search is unavailable right now`

type Props = {
  /** Closes the hosting modal; the shell passes `closeModal` from `app/lib/modal`. */
  onClose: () => void
  pageRows: PageRow[]
  /** Whether rows carry a location name: the workspace spans more than one location. */
  showLocation: boolean
}

/** The palette's content. The shell opens it through `showModal`, which owns Escape, backdrop, and focus. */
export const GlobalSearchDialog = observer(function GlobalSearchDialog({
  onClose,
  pageRows,
  showLocation,
}: Props): ReactElement | null {
  const user = useUser()
  if (!user) return null
  return (
    <GlobalSearchDialogBody user={user as UserJson} onClose={onClose} pageRows={pageRows} showLocation={showLocation} />
  )
})

const GlobalSearchDialogBody = observer(function GlobalSearchDialogBody({
  user,
  onClose,
  pageRows,
  showLocation,
}: Props & { user: UserJson }): ReactElement {
  const navigate = useNavigate()
  const [text, setText] = useState(``)
  const inputRef = useRef<HTMLInputElement>(null)

  // Ctrl+K while already open re-arrives on the bus; take it as "focus the box".
  useEffect(() => onOpenGlobalSearch(() => inputRef.current?.focus()), [])

  const query = parsePaletteQuery(text)
  const searchable = isSearchable(query)
  const search = useGlobalSearch(text, activitiesExclusionTerm(user))
  // Pages join in as soon as there is any text; an empty box shows the invitation instead.
  const matchedPages = text.trim() ? matchPageRows(pageRows, text) : []

  const showSeeAll = searchable && (search.activities.length > 0 || query.isNumberShaped)
  const noRecordsYet = search.contacts.length === 0 && search.activities.length === 0 && search.threads.length === 0
  const noRecords = searchable && !search.fetching && noRecordsYet
  const bothFailed = search.failed.contacts && search.failed.activities
  // First search in flight with nothing to show yet; later refetches keep the previous rows.
  const showSkeleton = search.fetching && noRecordsYet

  const contactRows: PaletteRow[] = search.contacts.map((contact) => ({ kind: `contact`, id: contact.id, contact }))
  const activityRows: PaletteRow[] = search.activities.map((activity) => ({
    kind: `activity`,
    id: activity.id,
    activity,
  }))
  const threadRows: PaletteRow[] = search.threads.map((thread) => ({ kind: `thread`, id: thread.threadId, thread }))
  const seeAllRow: PaletteRow = { kind: `see-all-activities`, id: `see-all-activities`, query: text.trim() }
  const createRow: PaletteRow = { kind: `create-contact`, id: `create-contact`, query: text.trim() }
  const pageItems: PaletteRow[] = matchedPages.map((page) => ({ kind: `page`, id: page.id, page }))

  // cmdk only re-selects on its own when nothing is selected, so on a query that also
  // matches a page alias the Pages row picked during the debounce would keep the
  // selection when contacts mount above it. Own the value and put it back on the head
  // row whenever the head changes.
  const rows: PaletteRow[] = [
    ...contactRows,
    ...activityRows,
    ...(showSeeAll ? [seeAllRow] : []),
    ...threadRows,
    ...pageItems,
    // Last, so a query that names a page still opens that page on Enter.
    ...(noRecords ? [createRow] : []),
  ]
  const headValue = rows.length > 0 ? paletteRowValue(rows[0]) : ``
  const [selected, setSelected] = useState(headValue)
  useEffect(() => {
    setSelected(headValue)
  }, [headValue])

  const openContact = (contact: ContactJson) => navigate(`/contacts?tab=all&drawer=${contact.id}`)

  // The palette closes first, and the creator takes the main modal slot rather than
  // nesting on a panel that is on its way out.
  const createContact = () => {
    onClose()
    showModal(
      <QuickCreateContactModal
        onCreate={openContact}
        onExistingContact={openContact}
        defaultName={query.isNumberShaped ? undefined : text.trim()}
        defaultCellPhone={query.isNumberShaped ? query.digits : undefined}
      />,
      { hideTitle: true, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
      false,
    )
  }

  const select = (row: PaletteRow) => {
    if (row.kind === `create-contact`) return createContact()
    openPaletteRow(navigate, row)
    onClose()
  }

  const item = (row: PaletteRow, content: ReactElement) => (
    <CommandItem
      key={paletteRowValue(row)}
      value={paletteRowValue(row)}
      onSelect={() => select(row)}
      className="min-h-[44px] md:min-h-0"
    >
      {content}
    </CommandItem>
  )

  return (
    <Command
      shouldFilter={false}
      loop
      value={selected}
      onValueChange={setSelected}
      // The input reads as a field of its own, inset above the results, rather than a
      // bare header row: its own border and background, and a focus ring while typing.
      className="[&_[cmdk-input-wrapper]]:rounded-md [&_[cmdk-input-wrapper]]:border [&_[cmdk-input-wrapper]]:border-default [&_[cmdk-input-wrapper]]:bg-surface [&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]]:focus-within:border-blue-500 [&_[cmdk-input-wrapper]]:focus-within:ring-2 [&_[cmdk-input-wrapper]]:focus-within:ring-blue-500/20 [&_[cmdk-input-wrapper]_svg]:mr-2 [&_[cmdk-input-wrapper]_svg]:text-subtle [&_[cmdk-input-wrapper]_svg]:opacity-100"
    >
      <div className="relative mx-3 mt-3">
        <CommandInput
          ref={inputRef}
          value={text}
          onValueChange={setText}
          placeholder={INPUT_PLACEHOLDER}
          autoFocus
          className="h-11 pr-12 text-sm text-strong placeholder:text-minimal"
        />
        <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center">
          {search.fetching ? (
            <Spinner size={4} />
          ) : (
            <kbd className="rounded border border-default bg-surface-subtle px-1.5 py-0.5 font-sans text-[11px] text-subtle">
              esc
            </kbd>
          )}
        </div>
      </div>
      {/* The floor matches the empty-state panel, so switching between the panel, the
              skeleton, and the first results never resizes the modal. */}
      <CommandList className="min-h-[260px] max-h-[60vh]">
        {!text.trim() && <PaletteEmptyState />}
        {showSkeleton && <PaletteSkeleton />}

        {contactRows.length > 0 && (
          <CommandGroup heading="Contacts">
            {contactRows.map((row) =>
              row.kind === `contact`
                ? item(row, <ContactRowContent contact={row.contact} query={query} showLocation={showLocation} />)
                : null,
            )}
          </CommandGroup>
        )}

        {(activityRows.length > 0 || showSeeAll) && (
          <CommandGroup heading="Activities">
            {activityRows.map((row) =>
              row.kind === `activity`
                ? item(row, <ActivityRowContent activity={row.activity} query={query} showLocation={showLocation} />)
                : null,
            )}
            {showSeeAll && item(seeAllRow, <SeeAllRowContent query={text.trim()} />)}
          </CommandGroup>
        )}

        {threadRows.length > 0 && (
          <CommandGroup heading="Threads">
            {threadRows.map((row) =>
              row.kind === `thread`
                ? item(row, <ThreadRowContent thread={row.thread} query={query} showLocation={showLocation} />)
                : null,
            )}
          </CommandGroup>
        )}

        {pageItems.length > 0 && (
          <CommandGroup heading="Pages">
            {pageItems.map((row) => (row.kind === `page` ? item(row, <PageRowContent page={row.page} />) : null))}
          </CommandGroup>
        )}

        {noRecords && (
          <>
            <p className="px-3 pt-3 pb-1 text-sm text-subtle">{bothFailed ? UNAVAILABLE_COPY : NO_MATCH_COPY}</p>
            <CommandGroup>{item(createRow, <CreateContactRowContent query={text.trim()} />)}</CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  )
})
